/**
 * TUTORIAL CONTEXT v2.1
 * ✅ fix 1: isOnboardingCompleted y tutorialStats con estado reactivo
 *           — antes eran useMemo sin deps, se calculaban una sola vez y nunca
 *             se actualizaban aunque el usuario completara el onboarding
 * ✅ fix 2: handleNext en TutorialOverlay estabilizado con useRef
 *           — closure stale en el listener de wait-for-action causaba que
 *             el índice del paso quedara congelado al valor inicial
 * ✅ fix 3: TutorialService encapsula mutaciones — saveProgress() siempre
 *           después de cualquier cambio, sin acceso directo a this.progress
 *           desde afuera del servicio
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import tutorialService, { TUTORIAL_STEPS } from '../services/tutorialService';
import { useAuth } from './AuthContext';
import { useFirebase } from './FirebaseContext';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) throw new Error('useTutorial must be used within TutorialProvider');
  return context;
};

export const TutorialProvider = ({ children }) => {
  const { user } = useAuth();
  const { userProfile } = useFirebase();

  const [showOnboarding, setShowOnboarding]         = useState(false);
  const [currentTooltip, setCurrentTooltip]         = useState(null);
  const [isTooltipVisible, setIsTooltipVisible]     = useState(false);
  const [highlightedElement, setHighlightedElement] = useState(null);

  // fix 1: estado reactivo para valores derivados del servicio
  const [onboardingCompleted, setOnboardingCompleted] = useState(
    () => tutorialService.isOnboardingCompleted()
  );
  const [stats, setStats] = useState(
    () => tutorialService.getStats()
  );

  const refreshDerivedState = useCallback(() => {
    setOnboardingCompleted(tutorialService.isOnboardingCompleted());
    setStats(tutorialService.getStats());
  }, []);

  // ============================================
  // INICIALIZACIÓN
  // Espera a que haya usuario autenticado Y hogar configurado
  // antes de mostrar el tutorial — sin estos dos el usuario
  // no puede cargar ejemplos ni interactuar con la app
  // ============================================
  useEffect(() => {
    const isReady = !!user && !!userProfile?.householdId;
    if (!isReady) return;
    if (tutorialService.isOnboardingCompleted()) return;

    const timer = setTimeout(() => setShowOnboarding(true), 1000);
    return () => clearTimeout(timer);
  }, [user, userProfile?.householdId]);

  // ============================================
  // HANDLERS
  // ============================================
  const completeStep = useCallback((stepId) => {
    tutorialService.completeStep(stepId);
    refreshDerivedState(); // fix 1: actualiza stats reactivos
    if (stepId === TUTORIAL_STEPS.ONBOARDING_COMPLETE) {
      setShowOnboarding(false);
    }
  }, [refreshDerivedState]);

  const completeOnboarding = useCallback(() => {
    tutorialService.completeOnboarding();
    refreshDerivedState(); // fix 1: actualiza onboardingCompleted reactivo
    setShowOnboarding(false);
  }, [refreshDerivedState]);

  const showTooltip = useCallback((tooltip, targetElement = null) => {
    setCurrentTooltip(tooltip);
    setHighlightedElement(targetElement);
    setIsTooltipVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    if (currentTooltip) {
      tutorialService.dismissTooltip(currentTooltip.id);
      refreshDerivedState();
    }
    setIsTooltipVisible(false);
    setCurrentTooltip(null);
    setHighlightedElement(null);
  }, [currentTooltip, refreshDerivedState]);

  const completeTooltip = useCallback((stepId) => {
    tutorialService.completeStep(stepId);
    refreshDerivedState();
    hideTooltip();
  }, [hideTooltip, refreshDerivedState]);

  const showPageTooltip = useCallback((page) => {
    const tooltip = tutorialService.getNextTooltipForPage(page);
    if (tooltip) {
      setTimeout(() => showTooltip(tooltip), 500);
    }
  }, [showTooltip]);

  const resetTutorial = useCallback(() => {
    tutorialService.reset();
    refreshDerivedState();
    setShowOnboarding(true);
    setCurrentTooltip(null);
    setIsTooltipVisible(false);
    setHighlightedElement(null);
  }, [refreshDerivedState]);

  const isStepCompleted = useCallback((stepId) => {
    return tutorialService.isStepCompleted(stepId);
  }, []);

  // ============================================
  // VALUE
  // ============================================
  const value = useMemo(() => ({
    showOnboarding,
    setShowOnboarding,
    currentTooltip,
    isTooltipVisible,
    highlightedElement,
    completeStep,
    completeOnboarding,
    showTooltip,
    hideTooltip,
    completeTooltip,
    showPageTooltip,
    resetTutorial,
    isStepCompleted,
    // fix 1: valores reactivos en lugar de useMemo sin deps
    isOnboardingCompleted: onboardingCompleted,
    tutorialStats: stats,
  }), [
    showOnboarding,
    currentTooltip,
    isTooltipVisible,
    highlightedElement,
    completeStep,
    completeOnboarding,
    showTooltip,
    hideTooltip,
    completeTooltip,
    showPageTooltip,
    resetTutorial,
    isStepCompleted,
    onboardingCompleted,
    stats,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};